import { CommonModule } from '@angular/common'
import { Component, Input, OnInit } from '@angular/core'
import { DynamicTextComponent } from '../dynamic-text/dynamic-text.component'
import { ChannelService } from '../../services/channel.service'
import { LocalChannel } from '../../interfaces/local-channel'
import { ChannelTrackData, ChannelTrackDataFlags } from '../../interfaces/channel-track-data'
import { TranslateModule, TranslateService } from '@ngx-translate/core'
import { EventService } from '../../services/event.service'
import { HTVButtonControllerDirective } from '../../app-button-controller.directive'
import { PAGE_KEY } from '../../constants/page.constants'

interface AudioItem {
  label: string
  index: number
  data?: ChannelTrackData
}

@Component({
  selector: 'app-marriott-audio',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  templateUrl: './marriott-audio.component.html',
  styleUrls: ['./marriott-audio.component.scss'],
})
export class MarriottAudioComponent extends DynamicTextComponent implements OnInit {
  audioTracks: ChannelTrackData[] = []
  currentAudioTrack?: ChannelTrackData

  currentIndex?: number
  currentItem?: AudioItem
  audioList: AudioItem[] = []

  @Input() override singleLine = true
  @Input() override allowOverflow = true

  override enableVisibilityControl = true

  readonly ChannelTrackDataFlags = ChannelTrackDataFlags

  constructor(
    private appButtonDirective: HTVButtonControllerDirective,
    private channelService: ChannelService,
    private eventService: EventService,
    private translateService: TranslateService
  ) {
    super()
  }

  override ngOnInit(): void {
    super.ngOnInit()

    this.pageService.updateSubject(PAGE_KEY.TRACK_MENU_OVERRIDE, true)
    this.registerEventListener()
    this.registerChannelListener()
  }

  reset(): void {
    this.audioTracks = []
    this.currentAudioTrack = undefined
    this.audioList = []
    this.currentIndex = undefined
    this.currentItem = undefined
  }

  registerEventListener(): void {
    this.subscriptions.add(
      this.eventService.keyDown.subscribe((key: string) => {
        switch (key) {
          case 'AUDIO':
            this.appButtonDirective.clearSelectedButtons()
            this.nextTrack()
            break
          default:
            break
        }
      })
    )
  }

  registerChannelListener(): void {
    this.subscriptions.add(
      this.channelService.currentChannel$.subscribe((channel: LocalChannel | undefined) => {
        this.reset()

        if (channel) {
          this.subscriptions.add(
            channel.channelData$.subscribe(channelData => {
              if (channelData) {
                if (JSON.stringify(this.audioTracks) != JSON.stringify(channelData.audioTracks)) {
                  this.audioTracks = channelData.audioTracks
                  this.audioList = channelData.audioTracks.map((e, index) => this.mapToAudioItem(e, index))
                }

                if (JSON.stringify(this.currentAudioTrack?.data) != JSON.stringify(channelData.currentAudioTrack)) {
                  if (channelData.currentAudioTrack) {
                    this.currentAudioTrack = channelData.currentAudioTrack
                  } else {
                    this.currentAudioTrack = undefined
                  }

                  const index = this.audioTracks.findIndex(e => e.index === this.currentAudioTrack?.index)
                  if (index > -1) {
                    this.currentIndex = index
                    this.currentItem = this.audioList[this.currentIndex]
                  }
                }
              } else {
                this.reset()
              }
            })
          )
        }
      })
    )
  }

  mapToAudioItem(data: ChannelTrackData, index: number): AudioItem {
    let label: string
    switch (data.lang) {
      case 'none':
      case 'yyy':
        label = this.translateService.instant('CHANNEL_SUBTITLE.NONE')
        break
      case 'qaa':
        label = this.translateService.instant('CHANNEL_SUBTITLE.ORIGINAL_VERSION.ABR')
        break
      case 'qad':
        label = this.translateService.instant('CHANNEL_SUBTITLE.AUDIO_DESCRIPTION.ABR')
        break
      default:
        label = data.lang
        break
    }
    if (data.flags.indexOf(ChannelTrackDataFlags.HEARING_IMPAIRED) > -1) {
      label = `${label}(${this.translateService.instant('CHANNEL_SUBTITLE.CLOSED_CAPTION.ABR')})`
    }
    if (data.flags.indexOf(ChannelTrackDataFlags.VISUAL_IMPAIRED) > -1) {
      label = `${label}(${this.translateService.instant('CHANNEL_SUBTITLE.AUDIO_DESCRIPTION.ABR')})`
    }
    return {
      label: label,
      index: index,
      data: data,
    }
  }

  selectTrack(item: AudioItem): void {
    if (item.data) {
      this.channelService.setAudioTrack(item.data)
    }
  }

  nextTrack(): void {
    let nextIndex
    let nextItem

    if (this.currentIndex != undefined) {
      if (this.currentIndex < this.audioList.length - 1) {
        nextIndex = this.currentIndex + 1
        nextItem = this.audioList[nextIndex]
      } else {
        nextIndex = 0
        nextItem = this.audioList[0]
      }
    } else {
      if (this.audioList.length > 0) {
        nextIndex = 0
        nextItem = this.audioList[0]
      }
    }

    if (nextItem) {
      if (nextItem.data) {
        this.channelService.setAudioTrack(nextItem.data)
      }
    }
  }
}
